import axios from 'axios';

import { server } from 'utils/constants';
import { getAuthToken, sessionExpired } from 'library/auth';
import toast from 'react-hot-toast';

const service = axios.create({
    baseURL: new URL(server).href,
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});

const INTERNET_ERROR = 'Internet connection error. Please check your connection and try again.';

function isAuthError(error) {
    const { response } = error;
    return response && response.status === 401;
}

function isInternetError(error) {
    return !error.response;
}

// function isForbiddenError(error) {
//     const { response } = error;
//     return response && response.status === 403;
// }

// function isPageNotFound(error) {
//     const { response } = error;
//     return response && response.status === 404;
// }

service.interceptors.request.use((config: any) => {
    const { skipAuth, ...params } = config.params || {};
    const token = skipAuth ? null : getAuthToken();
    const url = config.url.match(/\/$/) ? config.url : `${config.url}/`;
    const requestConfig = {
        ...config,
        url,
        params,
    };

    if (token) {
        return {
            ...requestConfig,
            headers: {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            },
        };
    }

    return {
        ...requestConfig,
        headers: config.headers,
    };
});

// Response Interceptor
service.interceptors.response.use(
    ({ data }) => data,
    async (error) => {
        if (!error) {
            return Promise.reject(new Error('There was an error.'));
        }
        if (isAuthError(error)) {
            sessionExpired()
            return Promise.reject(new Error('Session expired!'));
        }
        if (axios.isCancel(error) && error.message === 'request-cancel') {
            return new Promise(() => { });
        }
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }
        if (isInternetError(error)) {
            toast.error(INTERNET_ERROR, { id: 'internet-error' });
            return new Promise(() => { });
        }

        const { response = {} } = error;
        return Promise.reject(response.data || error);
    },
);

const cancelable = {};

export default function ({ cancelPrevious, ...config }) {
    if (cancelPrevious) {
        const key = `${config.method}-${config.url}`;
        const cancel = cancelable[key];
        if (cancel) cancel('request-cancel');
        return service({
            ...config,
            cancelToken: new axios.CancelToken((cancelRequest) => {
                cancelable[key] = cancelRequest;
            }),
        });
    }
    return service(config);
}

export function serverLink() {
    return server;
}
